import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Plane } from "lucide-react";
import { C, EASE, DEFAULT_PROJECTS, media, useContent, useSite } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";
import { useIsDesktop, useIsWide } from "./hooks";
import { playThud } from "./sound";

const pad = (n) => String(n).padStart(2, "0");
const brandOf = (p) => {
  const t = p.title || "";
  return /[—–\-·@]/.test(t) ? t.split(/[—–\-·@]/)[0].trim() : t.split(" ")[0];
};
const codeOf = (p) => {
  const first = brandOf(p).split(" ")[0];
  if (/^[A-Z]{2,3}$/.test(first)) return first;
  return brandOf(p).replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() || "SA";
};
const HEX = /^#[0-9A-Fa-f]{6}$/;
const colorOf = (p) => (HEX.test(p.color || "") ? p.color : C.burgundy);
const lum = (hex) => {
  const [r, g, b] = [1, 3, 5].map((k) => parseInt(hex.slice(k, k + 2), 16) / 255);
  return 0.299 * r + 0.587 * g + 0.114 * b;
};
const inkFor = (hex) => (lum(hex) > 0.62 ? C.wine : C.cream);
const PEEK = 64;

function Barcode({ color, n = 26 }) {
  return (
    <div className="flex gap-[2px] h-6 items-stretch" aria-hidden>
      {Array.from({ length: n }).map((_, k) => <span key={k} style={{ width: k % 3 ? 1 : k % 5 ? 2 : 3, background: color }} />)}
    </div>
  );
}

const FACE = { boxShadow: `0 -6px 18px ${C.wine}1f, 0 16px 28px ${C.wine}26` };

function PassFront({ p, i, color, ink }) {
  return (
    <div className="absolute inset-0 rounded-2xl overflow-hidden flex flex-col" style={{ background: color, ...FACE }}>
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 shrink-0" style={{ height: PEEK, borderBottom: `1.5px dashed ${ink}40` }}>
        <div className="min-w-0">
          <p className="ed-mono text-[8px] uppercase tracking-[0.28em] opacity-80" style={{ color: ink }}>SA{pad(i + 1)} · {p.tag}</p>
          <p className="ed-serif text-xl md:text-2xl leading-tight truncate" style={{ color: ink }}>{brandOf(p)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0" style={{ color: ink }}>
          <span className="ed-serif text-lg md:text-xl">NYU</span>
          <Plane size={14} />
          <span className="ed-serif text-lg md:text-xl">{codeOf(p)}</span>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 p-4 md:p-5 flex flex-col gap-1.5">
          <p className="ed-serif italic text-lg md:text-xl leading-tight line-clamp-2" style={{ color: ink }}>{p.title}</p>
          {p.subtitle && <p className="ed-mono text-[9px] uppercase tracking-[0.18em] line-clamp-1 opacity-80" style={{ color: ink }}>{p.subtitle}</p>}
          <p className="text-[13px] md:text-sm leading-snug line-clamp-3 mt-1" style={{ color: ink, opacity: 0.92 }} data-testid={`pass-body-${i}`}>{p.body}</p>
          {p.role && <p className="text-xs leading-snug line-clamp-2" style={{ color: ink }} data-testid={`pass-role-${i}`}><span className="ed-mono text-[8px] uppercase tracking-[0.2em] mr-1.5 opacity-75">My role</span>{p.role}</p>}
          <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t" style={{ borderColor: `${ink}33` }}>
            {p.team ? <span data-testid={`pass-team-${i}`} className="whitespace-nowrap ed-mono text-[8px] uppercase tracking-[0.12em] md:tracking-[0.18em] px-2 py-1 rounded-full" style={{ border: `1px solid ${ink}88`, color: ink }}>Team project</span> : <span className="whitespace-nowrap ed-mono text-[8px] uppercase tracking-[0.12em] md:tracking-[0.18em] opacity-75" style={{ color: ink }}>Solo flight</span>}
            {p.href && (
              <a href={p.href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`deck-open-link-${i}`} className="whitespace-nowrap inline-flex items-center gap-1 px-2.5 md:px-3.5 py-2 rounded-full ed-mono text-[8px] md:text-[9px] uppercase tracking-[0.12em] md:tracking-[0.22em] transition-transform duration-300 hover:-translate-y-0.5" style={{ background: ink, color }}>
                Open the deck <ArrowUpRight size={12} />
              </a>
            )}
          </div>
        </div>
        <div className="relative w-[30%] shrink-0 p-2.5 flex flex-col gap-2" style={{ background: C.warm, borderLeft: `2px dashed ${color}` }}>
          <div className="flex-1 min-h-0 rounded-lg overflow-hidden" style={{ background: C.blush }}>
            <img src={media(p.img)} alt={p.title} className="w-full h-full object-cover" />
          </div>
          <Barcode color={C.wine} n={14} />
          <p className="ed-mono text-[8px] uppercase tracking-[0.2em] text-center" style={{ color: C.muted }}>Gate {pad(i + 1)}</p>
        </div>
      </div>
    </div>
  );
}

function Pass({ p, i, last, open, onTap, h }) {
  const color = colorOf(p);
  const ink = inkFor(color);
  return (
    <motion.div
      initial={false}
      animate={{ height: open || last ? h + (last ? 0 : 14) : PEEK }}
      transition={{ duration: 0.6, ease: EASE }}
      className="relative"
      style={{ zIndex: i + 1 }}
    >
      <motion.div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-testid={`clean-project-${i}`}
        onClick={() => onTap(i)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTap(i); } }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        whileHover={open ? {} : { y: -8 }}
        transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
        className="absolute inset-x-0 top-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] rounded-2xl text-left"
        style={{ height: h }}
      >
        <div className="relative w-full h-full" data-testid={`boarding-pass-${i}-front`}>
          <PassFront p={p} i={i} color={color} ink={ink} />
        </div>
      </motion.div>
    </motion.div>
  );
}

function Stack({ items, offset, h, testid, sel, onTap }) {
  return (
    <div className="w-full" data-testid={testid}>
      {items.map((p, k) => (
        <Pass key={p.id || k} p={p} i={offset + k} h={h} last={k === items.length - 1} open={sel === offset + k} onTap={onTap} />
      ))}
    </div>
  );
}

function Board({ items, active, onPick }) {
  return (
    <div className="rounded-xl mb-8 overflow-hidden" style={{ background: C.wine }} data-testid="departures-board">
      <div className="flex items-center gap-3 px-4 py-2" style={{ borderBottom: `1px solid ${C.gold}33` }}>
        <Plane size={12} style={{ color: C.gold }} />
        <span className="ed-mono text-[9px] uppercase tracking-[0.35em]" style={{ color: C.goldLight }}>Departures</span>
        <span className="ed-mono text-[9px] uppercase tracking-[0.25em] ml-auto hidden sm:block" style={{ color: `${C.cream}80` }}>Pick a flight</span>
      </div>
      <div className="flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((p, i) => {
          const on = active.includes(i);
          const col = lum(colorOf(p)) < 0.2 ? C.goldLight : colorOf(p);
          return (
            <button key={p.id || i} data-testid={`departure-row-${i}`} onClick={() => onPick(i)} className="flex-1 min-w-[92px] px-3 py-2.5 text-left transition-colors duration-300 hover:bg-[#58111A]" style={{ background: on ? C.rich : "transparent", borderRight: `1px solid ${C.cream}12`, boxShadow: on ? `inset 0 -2px 0 ${col}` : "none" }}>
              <p className="ed-mono text-sm tracking-[0.15em]" style={{ color: col, filter: "brightness(1.3)" }}>{codeOf(p)}</p>
              <p className="ed-mono text-[8px] uppercase tracking-[0.22em] mt-0.5" style={{ color: on ? C.gold : `${C.cream}80` }}>{on ? "Boarding" : `Gate ${pad(i + 1)}`}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DecksSection() {
  const { projects } = useSite();
  const items = projects.length ? projects : DEFAULT_PROJECTS;
  const c = useContent("work");
  const desktop = useIsDesktop();
  const wide = useIsWide();
  const [head, ital] = splitHeading(c.heading, "Seven things I've actually", "made.");
  const h = desktop ? 330 : 350;
  const half = wide ? Math.ceil(items.length / 2) : items.length;
  const [sel, setSel] = useState({ a: 0, b: null });
  const selB = sel.b ?? half;
  const keyOf = (i) => (i < half ? "a" : "b");
  const current = { a: sel.a < half ? sel.a : 0, b: selB };
  const tap = (i) => {
    const k = keyOf(i);
    if (current[k] === i) return;
    setSel((s) => ({ ...s, [k]: i })); playThud();
  };
  const pick = (i) => {
    tap(i);
    setTimeout(() => document.querySelector(`[data-testid=clean-project-${i}]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
  };
  return (
    <section id="work" data-testid="clean-work" className="relative min-h-screen flex flex-col justify-center py-24 md:py-28 overflow-hidden" style={{ background: C.cream }}>
      <div className="max-w-[1440px] w-full mx-auto px-5 md:px-12">
        <SectionHead num="02" eyebrow={c.eyebrow || "Decks · Selected work"} heading={head} italic={ital} aside={c.subline || "Two wallets of boarding passes, one per project. Tap a pass to pull it out and open the deck."} />
        <Board items={items} active={wide ? [current.a, current.b] : [current.a]} onPick={pick} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 lg:gap-14 items-start" data-testid="pass-stack">
          <Stack items={items.slice(0, half)} offset={0} h={h} testid="pass-stack-a" sel={current.a} onTap={tap} />
          {wide && <Stack items={items.slice(half)} offset={half} h={h} testid="pass-stack-b" sel={current.b} onTap={tap} />}
        </div>
      </div>
    </section>
  );
}
